import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  DocumentsResponseDTO,
  GetInsurersRes,
  MortgageeRes,
  PoliceStationsDTO,
  RootResOfListOfVehicleMakeModelsDTO,
  VehicleMakeModelsDTO,
  RootResOfListOfString,
  RootResOfListOfOccupationDTO,
  RootResOfListOfGetPolicyFilesRes,
  GetFileLinkDataRes,
  RootResOfListOfParishTownsDTO,
  PolicyRenewParamDTO,
  PolicyRenewRes,
  DrawReceiptRes,
  DrawReceiptParamDTO,
  DocumentDTO,
  GetReceiptsRes,
  DocumentCreateRes,
  SingleReceiptRes,
  LocatonSearchRes,
  LocationSearchParamDTO,
  EBHighPerformanceVehicleListDTO
} from 'models/DTOs';
import { CoreApiService } from '@services/core-api-service';
import { API_URLS } from '../../constants/core.constants';
import { NotificationService } from '@services/notification.service';
import { UserProfile } from 'app/services/user.service';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class EpicBridgeService extends CoreApiService {
  private _notifications = inject(NotificationService);
  vehiclesSubject: BehaviorSubject<VehicleMakeModelsDTO[]> = new BehaviorSubject(null);

  getOccupations() {
    return this.get<RootResOfListOfOccupationDTO>(`${API_URLS.epicbridge}/getoccupation`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.data;
      })
    );
  }

  getMortgages() {
    return this.get<MortgageeRes>(`${API_URLS.epicbridge}/getmortgages`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.mortgagees;
      })
    );
  }

  getInsurers() {
    return this.get<GetInsurersRes>(`${API_URLS.epicbridge}/getinsurers`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.insurers;
      })
    );
  }

  getPoliceStations() {
    return this.get<PoliceStationsDTO>(`${API_URLS.epicbridge}/getpolicestations`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.policeStations;
      })
    );
  }

  getCertificates(policyId: string) {
    return this.get<DocumentsResponseDTO>(`${API_URLS.epicbridge}/getcertificate`, {
      params: { policyId: policyId }
    }).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.documents;
      })
    );
  }

  getCoverNote(policyId: string) {
    return this.get<DocumentsResponseDTO>(`${API_URLS.epicbridge}/getcovernote`, {
      params: { policyId: policyId }
    }).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.documents;
      })
    );
  }

  getCountries() {
    return this.get<RootResOfListOfString>(`${API_URLS.epicbridge}/countries`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.data;
      })
    );
  }

  getParishTowns() {
    return this.get<RootResOfListOfParishTownsDTO>(`${API_URLS.epicbridge}/country/parishtowns`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.data;
      })
    );
  }

  getRoofMaterials() {
    return this.get<RootResOfListOfString>(`${API_URLS.epicbridge}/getproprooftype`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.data;
      })
    );
  }

  getWallMaterials() {
    return this.get<RootResOfListOfString>(`${API_URLS.epicbridge}/getpropwalltype`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.data;
      })
    );
  }

  getVehColors() {
    return this.get<RootResOfListOfString>(`${API_URLS.epicbridge}/getvehcolour`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.data;
      })
    );
  }

  getHPV() {
    return this.get<EBHighPerformanceVehicleListDTO[]>(`${API_URLS.ebHighPerformanceVehicle}`).pipe(
      map(res => {
        return res;
      })
    );
  }

  getVehMakeModels() {
    return this.get<RootResOfListOfVehicleMakeModelsDTO>(`${API_URLS.epicbridge}/getvehmakemodels`).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        this.vehiclesSubject.next(res.data);
        return res.data;
      })
    );
  }

  getPolicyFiles(policyID: string) {
    return this.get<RootResOfListOfGetPolicyFilesRes>(API_URLS.ebPolicyFiles, {
      params: { policyId: policyID }
    }).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.data;
      })
    );
  }

  getFileLinkData(fileID: string) {
    return this.get<GetFileLinkDataRes>(API_URLS.ebFileLinkData, {
      params: { fileId: fileID }
    }).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.data;
      })
    );
  }

  policyRenew(parameters: PolicyRenewParamDTO): Observable<PolicyRenewRes> {
    return this.post<PolicyRenewRes>(`${API_URLS.epicbridge}/policy/renew`, parameters).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res;
      })
    );
  }

  drawReceipt(policyId: string, amount: number): Observable<DrawReceiptRes> {
    const parameters: DrawReceiptParamDTO = {
      policyId: policyId,
      amount: amount,
      autoAuthorize: true,
      lodgeReceipt: true,
      branch: 'Webportal',
      currency: 'CI',
      paymentType: 'Credit Cards',
      receiptDate: null,
      clientGlobalNameId: null,
      manualReceiptNumber: null,
      source: null
    };

    return this.post<DrawReceiptRes>(`${API_URLS.epicbridge}/drawreceipt`, parameters).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res;
      })
    );
  }

  getUserReceipts(user: UserProfile, startDate?: string, endDate?: string): Observable<GetReceiptsRes> {
    return this.post<GetReceiptsRes>(`${API_URLS.epicbridge}/getreceipts`, {
      startDate: startDate,
      endDate: endDate,
      globalNameSearchParams: {
        firstName: user.firstName,
        lastName: user.lastName,
        dateOfBirth: formatDate(user.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
        isACompany: false,
        // nationalIdType: this.getNationalIdType(user.nationalIDType),
        // nationalId: user.nationalIDTypeNumber,
        email: user.email,
        gender: user.genderType,
        returnGlobalNameObject: false,
        returnMainLocationOnly: false
      }
    }).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res;
      })
    );
  }

  singleReceipt(receiptId: string, includePdf: boolean): Observable<SingleReceiptRes> {
    return this.get<SingleReceiptRes>(`${API_URLS.epicbridge}/singlereceipt`, {
      params: { receiptId: receiptId, includePdf: includePdf }
    }).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res;
      })
    );
  }

  getQuotationDocument(quoteNumber: number, policyType: string): Observable<DocumentCreateRes> {
    return this.get<DocumentCreateRes>(`${API_URLS.epicbridge}/getquotationdocument/${quoteNumber}`, {
      params: { policyType: policyType }
    }).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res;
      })
    );
  }

  createEBFileBlob(ebFile: DocumentDTO): File {
    const bstr = atob(ebFile.data);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], ebFile.fileName ?? 'File', { type: ebFile.mimeType ?? 'application/pdf' });
  }

  getNationalIdType(type: string): string {
    switch (type) {
      case 'Passport':
        return 'PPort';
      case 'Driver License':
        return 'DLNo';
      case 'ID':
        return 'NID';
      default:
        return type;
    }
  }

  getDiscountCode(coverageType: string, discountPeriod: string): string {
    switch (coverageType) {
      case 'PMC':
        switch (discountPeriod) {
          case '1 year':
            return '401';
          case '2 years':
            return '402';
          case '3 years':
            return '403';
          case '4 years':
            return '404';
          case '5 years':
            return '405';
          default:
            return '';
        }
      case 'PMT':
        switch (discountPeriod) {
          case '1 year':
            return '400';
          case '2 years':
            return '412';
          case '3 years':
            return '411';
          default:
            return '';
        }
      case 'PMTL':
        switch (discountPeriod) {
          case '1 year':
            return '406';
          case '2 years':
            return '409';
          case '3 years':
            return '407';
          case '4 years':
            return '410';
          case '5 years':
            return '408';
          default:
            return '';
        }
    }
  }

  getDiscountValue(discountCode: string): number {
    switch (discountCode) {
      case '400':
        return 10.0;
      case '401':
        return 20.0;
      case '402':
        return 30.0;
      case '403':
        return 40.0;
      case '404':
        return 50.0;
      case '405':
        return 60.0;
      case '406':
        return 15.0;
      case '407':
        return 35.0;
      case '408':
        return 50.0;
      case '409':
        return 25.0;
      case '410':
        return 45.0;
      case '411':
        return 30.0;
      case '412':
        return 20.0;
      default:
        return null;
    }
  }

  ebLocationSearch(locationSearch: string): Observable<LocatonSearchRes> {
    const params: LocationSearchParamDTO = {
      searchString: locationSearch,
      excludePoBoxes: null,
      block: null,
      buildingLabel: null,
      buildingNumber: null,
      complexName: null,
      streetNumber: null,
      streetName: null,
      streetType: null,
      unitNumber: null,
      generalArea: null,
      town: null,
      parish: null,
      island: null,
      internationalArea: null,
      country: null
    };
    
    return this.post<LocatonSearchRes>(API_URLS.ebLocationSearch, params);
  }
}
